import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { BulbOutline, MoreVertical } from '@lyearn/icons';
import { TypographyWithTooltip } from '@lyearn/molecules';
import { Avatar, Stack, Typography } from '@lyearn/ui';
import { TypographyColors, TypographyVariants } from '@lyearn/ui';
import useNavigation from '@/hooks/useNavigation';
import { ContentsFragment } from '@/modules/corporate/modules/admin/graphql/fragments/generated/Contents';
import { convertKnowledgeAssetSubTypeVariant } from '@/modules/knowledge-asset/types';
import routes from '@/pages/corporate/routes';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import { KnowledgeAssetContentType, ResourceStatusEnum } from '@/types/schema';

import { logoStyle } from '../../styles';
import { getClassifiedChildKnowledgeAssets } from '../../utils';
import { renderKnowledgeAssetStatusPill } from '../StatusPill';

export interface UserDetailsOverrides {
  Email?: {
    Typography?: {
      variant?: TypographyVariants;
      color?: TypographyColors;
    };
  };
  Name?: {
    Typography?: {
      variant?: TypographyVariants;
      color?: TypographyColors;
    };
  };
  Icon?: {
    className?: string;
    size?: number;
  };
}

function CustomerStoryCardGrid({
  customerStory,
  index,
  hideActionMenu = true,
  isLearner,
  editOnClick,
  showStatusPill = false,
}: {
  customerStory: ContentsFragment;
  index: number;
  hideActionMenu?: boolean;
  isLearner?: boolean;
  editOnClick?: boolean;
  showStatusPill?: boolean;
}) {
  const { t } = useTranslation();
  const { id, name, cover } = customerStory;
  const industries = (customerStory as KnowledgeAssetContentType).industries?.map((industry) =>
    convertKnowledgeAssetSubTypeVariant(industry),
  );
  const isAdminRoute = useAdminRoute();
  const industriesString =
    industries && industries.length
      ? industries?.[0] + (industries?.length > 1 ? ` +${industries?.length - 1}` : '')
      : null;

  const navigation = useNavigation();
  const onClick = useCallback(() => {
    if (editOnClick) {
      navigation.replace(routes.KnowledgeAssetEditLearner(id, 'customer-story'), {
        appendQuery: false,
      });
      return;
    }
    if (isLearner || !isAdminRoute) {
      navigation.push(routes.KnowledgeAssetLearnerDetails(id, 'customer-story'), {
        appendQuery: false,
      });
      return;
    }
    const isDraft =
      (customerStory as KnowledgeAssetContentType).status === ResourceStatusEnum.Draft;
    if (isDraft) {
      navigation.replace(routes.KnowledgeAssetEditor(id, 'customer-story'), { appendQuery: false });
    } else {
      navigation.replace(routes.KnowledgeAssetDetails(id, 'customer-story'), {
        appendQuery: false,
      });
    }
  }, [id, navigation, isLearner, editOnClick, isAdminRoute, routes]);

  const { solutions } = getClassifiedChildKnowledgeAssets(
    customerStory as KnowledgeAssetContentType,
  );
  const solutionStr = solutions.length ? solutions?.[0] : 'No solution';
  const plusN = solutions && solutions?.length > 1 ? ` +${solutions?.length - 1}` : null;

  const statusPill = useMemo(
    () =>
      showStatusPill
        ? renderKnowledgeAssetStatusPill(customerStory as KnowledgeAssetContentType, t)
        : null,
    [customerStory, showStatusPill, t],
  );

  return (
    <Stack
      className="w-[23.2rem] rounded-12 border cursor-pointer border-field px-20 py-20 hover:shadow-01 transition-shadow xs:min-h-full xs:w-full"
      direction="col"
      gapY={16}
      onClick={onClick}>
      <Stack gapX={12} justifyContent="between">
        <Avatar className={logoStyle} size={56} src={cover?.url || ''} />
        {hideActionMenu ? null : (
          <Stack className="flex-shrink-0">
            <MoreVertical className="text-icon-secondary" height={20} width={20} />
          </Stack>
        )}
      </Stack>
      <Stack direction="col">
        <Stack className="w-full overflow-hidden">
          <TypographyWithTooltip numberOfLines={1} variant="heading-xs">
            {name}
          </TypographyWithTooltip>
        </Stack>
        <Stack>
          <Typography color="text-text-secondary" numberOfLines={1} variant="label-02">
            {industriesString}
          </Typography>
        </Stack>
      </Stack>

      {statusPill ? (
        statusPill
      ) : solutionStr ? (
        <Stack
          alignItems="center"
          className="bg-bg-tertiary rounded-24 px-14 mt-auto w-fit h-[3.6rem]"
          direction="row"
          gapX={6}
          justifyContent="start">
          {solutions.length ? (
            <Stack className="flex-shrink-0">
              <BulbOutline fillColorToken="icon-primary" height={16} width={16} />
            </Stack>
          ) : null}
          <Stack className="max-w-[12rem]">
            <TypographyWithTooltip
              as="span"
              color={solutions.length ? 'text-text-primary' : 'text-text-secondary'}
              numberOfLines={1}
              variant="body-short-02">
              {solutionStr}
            </TypographyWithTooltip>
          </Stack>
          {plusN ? (
            <Typography
              className="flex-shrink-0 me-6"
              color="text-text-primary"
              numberOfLines={1}
              variant="body-short-02">
              {plusN}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
}

export default CustomerStoryCardGrid;
